import React, { FC, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import './Login.css';
import AuthApi from '../../../API/auth/authApi';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Button from '../../../components/bootstrap/Button';
import Logo from '../../../components/Logo';
import Spinner from '../../../components/bootstrap/Spinner';
import Alert from '../../../components/bootstrap/Alert';
import useDarkMode from '../../../hooks/useDarkMode';
import AuthContext from '../../../contexts/authContext';
import USERS from '../../../common/data/userDummyData';
import { axiosWi } from '../../../API/axiosInstance';
import { loginToReCity } from '../../../API/auth/recityAuthApi';

const Login: FC = () => {
	const { setUser, setToken } = useContext(AuthContext);
	const { darkModeStatus } = useDarkMode();
	const navigate = useNavigate();

	const [isLoading, setIsLoading] = useState(false);
	const [loginError, setLoginError] = useState<string>('');

	const formik = useFormik({
		initialValues: {
			loginUsername: '',
			loginPassword: '',
		},
		validate: (values) => {
			const errors: any = {};
			if (!values.loginUsername) errors.loginUsername = 'Required';
			if (!values.loginPassword) errors.loginPassword = 'Required';
			return errors;
		},
		onSubmit: async (values) => {
			setIsLoading(true);
			setLoginError('');

			try {
				// Step 1: Login API call
				const res = await AuthApi.loginApi({
					username: values.loginUsername,
					password: values.loginPassword,
				});

				if (!res.data?.token) {
					throw new Error('No token received from server');
				}

				// Step 2: Set auth context
				setUser(JSON.stringify(res.data));
				setToken(res.data.token);

				// Step 3: Optional - Login to Recity
				try {
					await loginToReCity(values.loginUsername, values.loginPassword);
				} catch (e) {
					console.warn('Recity login failed, continuing with main login');
				}

				// Step 4: Fetch cities and store data
				await handlePostLogin(res.data);
			} catch (err: any) {
				console.error('Login error:', err);
				const errorMessage = err?.response?.data?.message || err?.message || 'Login failed';
				setLoginError(errorMessage);
				formik.setFieldError('loginUsername', errorMessage);
			} finally {
				setIsLoading(false);
			}
		},
	});

	const handlePostLogin = async (loginResponse: any) => {
		try {
			// Fetch all cities
			const citiesResponse = await axiosWi.get('city/total-cities');
			const allCities = citiesResponse.data?.data || [];

			// Get the city from login response (could be 'ponda' or 'Ponda')
			const userCity = loginResponse.city || loginResponse.loggedInCity || '';

			// Store allCities in localStorage
			localStorage.setItem('allCities', JSON.stringify(allCities));

			// Store loggedInCity (preserve case or normalize)
			localStorage.setItem('loggedInCity', userCity);

			// Store user info
			if (loginResponse) {
				localStorage.setItem('userInfo', JSON.stringify(loginResponse));
			}

			// Store user role
			const role = loginResponse.userRole || loginResponse.role || '';
			if (role) {
				localStorage.setItem('userRole', role);
			}

			// Store auth data in the format expected by CityProvider
			const authData = {
				loggedInName:
					loginResponse.loggedInName ||
					loginResponse.name ||
					loginResponse.username ||
					'',
				loggedInUserId:
					loginResponse.loggedInUserId || loginResponse.userId || loginResponse.id || '',
				userRole: role,
				allCities: allCities,
				loggedInCity: userCity,
			};
			localStorage.setItem('authData', JSON.stringify(authData));

			console.log('Login successful, data stored:', {
				allCities: allCities.length,
				loggedInCity: userCity,
				userRole: role,
			});

			// Dispatch event to notify CityProvider
			window.dispatchEvent(new Event('authDataChanged'));

			// Navigate to dashboard
			navigate('/dashboard');
		} catch (error) {
			console.error('Error in post-login setup:', error);
			// Still navigate to dashboard even if city fetch fails
			navigate('/dashboard');
		}
	};

	return (
		<PageWrapper isProtected={false} title='Login' className='p-0'>
			<div className='d-flex min-vh-100 w-100 align-items-center justify-content-center bg-login'>
				<div className='login-container'>
					<Card className='login-card'>
						<CardBody className='p-4'>
							<div className='text-center mb-4'>
								<Logo width={180} />
								<h2 className='fw-bold mt-3'>Welcome Back</h2>
								<p className='text-muted'>Sign in to your account</p>
							</div>

							{loginError && (
								<Alert color='danger' isLight icon='Error'>
									{loginError}
								</Alert>
							)}

							<Alert isLight icon='Lock' className='mb-4'>
								<strong>Demo:</strong> {USERS.JOHN.username} / {USERS.JOHN.password}
							</Alert>

							<form onSubmit={formik.handleSubmit} className='row g-4'>
								<div className='col-12'>
									<FormGroup id='loginUsername' label='Username or Email'>
										<Input
											value={formik.values.loginUsername}
											onChange={formik.handleChange}
											onBlur={formik.handleBlur}
											isTouched={formik.touched.loginUsername}
											invalidFeedback={formik.errors.loginUsername}
											placeholder='Enter your username'
										/>
									</FormGroup>
								</div>

								<div className='col-12'>
									<FormGroup id='loginPassword' label='Password'>
										<Input
											type='password'
											value={formik.values.loginPassword}
											onChange={formik.handleChange}
											onBlur={formik.handleBlur}
											isTouched={formik.touched.loginPassword}
											invalidFeedback={formik.errors.loginPassword}
											placeholder='Enter your password'
										/>
									</FormGroup>
								</div>

								<div className='col-12'>
									<Button
										type='submit'
										color='warning'
										className='w-100 py-3'
										isDisable={isLoading}>
										{isLoading ? (
											<>
												<Spinner size='sm' className='me-2' />
												Logging in...
											</>
										) : (
											'Login'
										)}
									</Button>
								</div>
							</form>
						</CardBody>
					</Card>
				</div>
			</div>
		</PageWrapper>
	);
};

export default Login;
